
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import { Calendar, User, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { BlogPost } from "@/types/blog";
import { blogService } from "@/services/blogService";
import SEOHead from "@/components/common/SEOHead";
import { getBlogCoverImage } from "@/utils/blogImages";

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const posts = await blogService.listPublished();
        setBlogPosts(posts);
      } catch (error: any) {
        console.error("Error loading posts:", error);
        setError(error?.message ?? "Erro ao carregar posts");
      } finally {
        setIsLoading(false);
      }
    };

    loadPosts();
  }, []);

  const featuredPost = blogPosts[0];
  const otherPosts = blogPosts.slice(1);

  const getCover = (post: BlogPost) => getBlogCoverImage(post);

  const getAuthor = (post: BlogPost) => post.author || "Equipe Toc Toc";

  const formatDate = (value?: string | null) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleDateString("pt-BR");
    } catch (err) {
      return value;
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog TocToc | Fotografia Imobiliária e Marketing",
    "description": "Dicas profissionais de fotografia imobiliária, marketing digital, CRM e vendas para Balneário Camboriú, Itajaí, Itapema e Porto Belo.",
    "url": "https://toctocbr.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "TocToc Fotografia Imobiliária",
      "logo": {
        "@type": "ImageObject",
        "url": "https://toctocbr.com/lovable-uploads/24e0e29e-3e10-43db-8be9-1cc4bc18ab2f.png"
      }
    },
    "about": [
      "Fotografia Imobiliária",
      "Marketing Digital Imobiliário", 
      "CRM para Imobiliárias",
      "Vendas de Imóveis"
    ]
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <TocTocHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF5605]"></div>
        </main>
        <TocTocFooter />
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Blog Fotografia Imobiliária | Dicas de Marketing e Vendas | Balneário Camboriú SC"
        description="📚 Blog especializado em fotografia imobiliária, marketing digital, CRM e vendas de imóveis. Dicas profissionais para Balneário Camboriú, Itajaí, Itapema e Porto Belo. Estratégias que aceleram vendas e arquitetura. Conteúdo exclusivo TocToc! 🏠💡"
        keywords="blog fotografia imobiliária, dicas marketing imobiliário Balneário Camboriú, estratégias vendas imóveis Itajaі, CRM imobiliário dicas Itapema, arquitetura marketing digital, fotografia profissional tips"
        canonicalUrl="/blog"
        schema={schema}
      />
      
      <div className="min-h-screen flex flex-col">
        <TocTocHeader />
        <main className="flex-1">
          {/* Hero Section */}
          <section className="py-16 md:py-24 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Blog <span className="text-[#FF5605]">Fotografia Imobiliária</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed">
                  Dicas profissionais de fotografia imobiliária, marketing digital, CRM e estratégias 
                  de vendas para Balneário Camboriú, Itajaí, Itapema e Porto Belo. 
                  Conteúdo exclusivo que acelera seus resultados no mercado imobiliário.
                </p>
              </div>
            </div>
          </section>

          {error && (
            <div className="container max-w-5xl mx-auto px-4 py-6">
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
                {error}
              </div>
            </div>
          )}

          {/* Featured Post */}
          {featuredPost && (
            <section className="py-16 bg-white">
              <div className="container max-w-7xl mx-auto px-4">
                <div className="bg-gradient-to-r from-[#F6E5C9]/30 to-[#F6E5C9]/10 rounded-3xl overflow-hidden">
                  <div className="grid lg:grid-cols-2 gap-8 items-center p-8 lg:p-12">
                    <div>
                      <span className="inline-block px-4 py-2 bg-[#FF5605] text-white text-sm font-medium rounded-full mb-4">
                        Post em Destaque
                      </span>
                      <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-4">
                        {featuredPost.title}
                      </h2>
                      <p className="text-lg text-[#5B5A59] mb-6">
                        {featuredPost.excerpt}
                      </p>
                      <div className="flex items-center space-x-6 mb-6">
                        <div className="flex items-center space-x-2">
                          <User className="h-5 w-5 text-[#FF5605]" />
                          <span className="text-[#5B5A59]">{getAuthor(featuredPost)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-5 w-5 text-[#FF5605]" />
                          <span className="text-[#5B5A59]">{formatDate(featuredPost.published_at)}</span>
                        </div>
                      </div>
                      <Button asChild className="bg-[#FF5605] hover:bg-[#E14D04] text-white group">
                        <Link to={`/blog/${featuredPost.slug}`} aria-label={`Ler o post ${featuredPost.title}`}>
                          Leia Mais
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </div>
                    <Link
                      to={`/blog/${featuredPost.slug}`}
                      className="rounded-2xl overflow-hidden block"
                      aria-label={`Ver detalhes do post ${featuredPost.title}`}
                    >
                      <img
                        src={getCover(featuredPost)}
                        alt={featuredPost.title}
                        className="w-full h-80 object-cover hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Blog Grid */}
          <section className="py-16 bg-gray-50">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-4">
                  Últimas <span className="text-[#FF5605]">Publicações</span>
                </h2>
                <p className="text-lg text-[#5B5A59] max-w-2xl mx-auto">
                  Explore nossos artigos mais recentes com dicas valiosas para o mercado imobiliário 
                  em Balneário Camboriú, Itajaí, Itapema e Porto Belo.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {otherPosts.map((post) => (
                  <article
                    key={post.id ?? post.slug}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      className="relative overflow-hidden block"
                      aria-label={`Ver detalhes do post ${post.title}`}
                    >
                      <img
                        src={getCover(post)}
                        alt={post.title}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-[#FF5605] text-white text-xs font-medium rounded-full">
                          Conteúdo Toc Toc
                        </span>
                      </div>
                    </Link>

                    <div className="p-6 flex flex-col h-full">
                      <Link to={`/blog/${post.slug}`} className="block" aria-label={`Ler o post ${post.title}`}>
                        <h3 className="text-xl font-bold text-[#323031] mb-3 group-hover:text-[#FF5605] transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                      </Link>

                      <p className="text-[#5B5A59] mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      <div className="mt-auto flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-[#5B5A59]">
                          <div className="flex items-center space-x-1">
                            <User className="h-4 w-4" />
                            <span>{getAuthor(post)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>{formatDate(post.published_at)}</span>
                          </div>
                        </div>

                        <Button
                          asChild
                          variant="ghost"
                          className="text-[#FF5605] hover:text-[#E14D04] p-0 h-auto group"
                        >
                          <Link to={`/blog/${post.slug}`} className="inline-flex items-center" aria-label={`Ler o post ${post.title}`}>
                            Leia Mais
                            <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Newsletter CTA */}
          <section className="py-16 bg-[#323031] text-white">
            <div className="container max-w-7xl mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Não perca nenhuma <span className="text-[#FF5605]">novidade</span>!
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Assine nossa newsletter e receba as melhores dicas de fotografia 
                imobiliária, marketing digital e estratégias de vendas para SC.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <input 
                  type="email" 
                  placeholder="Seu melhor e-mail"
                  className="flex-1 px-4 py-3 rounded-xl text-[#323031] focus:outline-none focus:ring-2 focus:ring-[#FF5605]"
                />
                <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-8 py-3 rounded-xl">
                  Assinar Newsletter
                </Button>
              </div>
            </div>
          </section>
        </main>
        <TocTocFooter />
      </div>
    </>
  );
};

export default Blog;
